import React, { useEffect, useState } from 'react';
import { Mail, Save, Send, Eye, EyeOff, CheckCircle, AlertTriangle } from 'lucide-react';
import { useApi } from './DashboardLayout';

export default function SmtpPage() {
  const [config, setConfig] = useState({
    host: '', port: 587, username: '', password: '', use_tls: true,
    sender_email: '', sender_name: '', reply_email: '', reply_name: '',
  });
  const [passwordSet, setPasswordSet] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const { apiFetch } = useApi();

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    const r = await apiFetch('/api/settings/smtp');
    const d = await r?.json();
    if (d) {
      setConfig(prev => ({ ...prev, ...d, password: '' }));
      setPasswordSet(d.password_set || false);
    }
  };

  const showFb = (type, text) => { setFeedback({ type, text }); setTimeout(() => setFeedback(null), 5000); };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const r = await apiFetch('/api/settings/smtp', { method: 'PUT', body: JSON.stringify(config) });
      if (!r) { setSaving(false); return; }
      const text = await r.text();
      const d = JSON.parse(text);
      if (d?.status) { showFb('success', 'SMTP-Einstellungen gespeichert.'); if (config.password) setPasswordSet(true); }
      else showFb('error', d?.detail || 'Fehler beim Speichern.');
    } catch (e) {
      showFb('error', 'Fehler beim Speichern: ' + (e.message || 'Unbekannter Fehler'));
    }
    setSaving(false);
  };

  const sendTest = async () => {
    if (!testEmail) { showFb('error', 'Bitte Empfänger-E-Mail eingeben.'); return; }
    setTesting(true);
    try {
      const r = await apiFetch('/api/settings/smtp/test', { method: 'POST', body: JSON.stringify({ recipient: testEmail }) });
      if (!r) { setTesting(false); return; }
      const text = await r.text();
      const d = JSON.parse(text);
      if (d?.status) showFb('success', d.message);
      else showFb('error', d?.detail || 'Fehler beim Senden.');
    } catch (e) {
      showFb('error', 'SMTP-Fehler: ' + (e.message || 'Verbindung fehlgeschlagen.'));
    }
    setTesting(false);
  };

  const set = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));

  return (
    <div data-testid="smtp-page">
      <div className="page-header">
        <h2>Einstellungen</h2>
        <p>E-Mail (SMTP) Konfiguration</p>
      </div>

      {feedback && (
        <div className={`design-feedback ${feedback.type}`} data-testid="smtp-feedback">
          {feedback.type === 'success' ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
          {feedback.text}
        </div>
      )}

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Mail size={20} style={{ color: 'var(--text-secondary)' }} />
            <h4 style={{ fontSize: 16, fontWeight: 700 }}>E-Mail Konfiguration (SMTP)</h4>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24 }}>
            Für den Versand von Lizenzen und Zertifikaten
          </p>

          {/* SERVER */}
          <h5 className="smtp-section-title">SERVER</h5>
          <div className="smtp-row">
            <div className="smtp-field" style={{ flex: 2 }}>
              <label className="smtp-label">SMTP Server</label>
              <input className="form-input" placeholder="smtp.example.de" value={config.host} onChange={e => set('host', e.target.value)} data-testid="smtp-host" />
            </div>
            <div className="smtp-field" style={{ flex: 1 }}>
              <label className="smtp-label">Port</label>
              <input className="form-input" type="number" value={config.port} onChange={e => set('port', parseInt(e.target.value) || 0)} data-testid="smtp-port" />
            </div>
          </div>
          <div className="smtp-row">
            <div className="smtp-field">
              <label className="smtp-label">Benutzername</label>
              <input className="form-input" value={config.username} onChange={e => set('username', e.target.value)} data-testid="smtp-username" />
            </div>
            <div className="smtp-field">
              <label className="smtp-label">Passwort</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="form-input"
                  type={showPw ? 'text' : 'password'}
                  value={config.password}
                  onChange={e => set('password', e.target.value)}
                  placeholder={passwordSet ? '(gespeichert)' : ''}
                  data-testid="smtp-password"
                  style={{ paddingRight: 40 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex' }}
                  data-testid="smtp-toggle-pw"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={config.use_tls} onChange={e => set('use_tls', e.target.checked)} style={{ accentColor: 'var(--primary)' }} data-testid="smtp-tls" />
            TLS verwenden (empfohlen)
          </label>

          {/* ABSENDER */}
          <h5 className="smtp-section-title" style={{ marginTop: 28 }}>ABSENDER</h5>
          <div className="smtp-row">
            <div className="smtp-field">
              <label className="smtp-label">Absender E-Mail</label>
              <input className="form-input" type="email" placeholder="noreply@bgpr.org" value={config.sender_email} onChange={e => set('sender_email', e.target.value)} data-testid="smtp-sender-email" />
            </div>
            <div className="smtp-field">
              <label className="smtp-label">Absender Name</label>
              <input className="form-input" placeholder="BGPR Verwaltung" value={config.sender_name} onChange={e => set('sender_name', e.target.value)} data-testid="smtp-sender-name" />
            </div>
          </div>

          {/* REPLY-TO */}
          <h5 className="smtp-section-title" style={{ marginTop: 28 }}>ANTWORTADRESSE (REPLY-TO)</h5>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
            Wenn Sie mit einer noreply-Adresse versenden, können Empfänger an diese Adresse antworten.
          </p>
          <div className="smtp-row">
            <div className="smtp-field">
              <label className="smtp-label">Antwort-E-Mail</label>
              <input className="form-input" type="email" placeholder="info@bgpr.org" value={config.reply_email} onChange={e => set('reply_email', e.target.value)} data-testid="smtp-reply-email" />
            </div>
            <div className="smtp-field">
              <label className="smtp-label">Antwort-Name</label>
              <input className="form-input" placeholder="BGPR e.V." value={config.reply_name} onChange={e => set('reply_name', e.target.value)} data-testid="smtp-reply-name" />
            </div>
          </div>

          <button className="btn btn-primary" style={{ marginTop: 24 }} onClick={saveConfig} disabled={saving} data-testid="smtp-save-btn">
            <Save size={14} /> Speichern
          </button>
        </div>
      </div>

      {/* TEST */}
      <div className="card">
        <div className="card-body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Send size={18} style={{ color: 'var(--text-secondary)' }} />
            <h4 style={{ fontSize: 16, fontWeight: 700 }}>SMTP-Verbindung testen</h4>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
            Senden Sie eine Test-E-Mail um die Konfiguration zu prüfen. Bitte speichern Sie die Einstellungen zuerst.
          </p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
            <div style={{ flex: 1, maxWidth: 360 }}>
              <label className="smtp-label">Empfänger E-Mail</label>
              <input className="form-input" type="email" placeholder="ihre@email.de" value={testEmail} onChange={e => setTestEmail(e.target.value)} data-testid="smtp-test-email" />
            </div>
            <button className="btn btn-secondary" onClick={sendTest} disabled={testing} data-testid="smtp-test-btn" style={{ height: 40 }}>
              <Send size={14} /> {testing ? 'Sende...' : 'Test senden'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
